import React, { useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  GripVertical,
  Plus,
  Trash2,
  Upload,
  Workflow,
} from 'lucide-react';
import {
  DEFAULT_PIPELINE,
  PIPELINE_PRESETS,
  applyPipelineTemplate,
  createStage,
  exportPipelineTemplate,
  parsePipelineTemplate,
  serialisePipelineTemplate,
  stageColor,
  stagesLostByOverwrite,
  type PipelineMergeStrategy,
  type PipelineStage,
} from '../../../lib/programPipeline';
import type { ProgramConfig } from '../../../lib/programConfig';
import { dragRowClass, moveItem, useDragReorder } from './dragReorder';

/**
 * Master Pipeline editor — one independent workflow per program.
 *
 * Replaces the single hardcoded 10-stage `MASTER_STAGES` array that every
 * program shared, so a study-abroad program no longer moves through DS-2019
 * and visa stages that do not apply to it.
 *
 * Includes the template tooling from the brief: copy a pipeline from another
 * program, duplicate the current one, import/export JSON, and choose whether
 * to overwrite or merge.
 */

interface Props {
  program: ProgramConfig;
  /** Other programs in the workspace — sources for "copy from". */
  allPrograms: ProgramConfig[];
  onChange: (patch: { pipeline: PipelineStage[]; defaultStageId?: string }) => void;
  disabled?: boolean;
}

export default function PipelineEditor({ program, allPrograms, onChange, disabled }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [strategy, setStrategy] = useState<PipelineMergeStrategy>('merge');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [pendingWarning, setPendingWarning] = useState<{
    stages: PipelineStage[];
    lost: PipelineStage[];
    label: string;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** A program with no configured pipeline inherits the platform default. */
  const usingDefault = (program.pipeline || []).length === 0;
  const stages = usingDefault ? DEFAULT_PIPELINE : program.pipeline;

  const otherPrograms = useMemo(
    () => allPrograms.filter((p) => p.id !== program.id && (p.pipeline || []).length > 0),
    [allPrograms, program.id]
  );

  const commit = (next: PipelineStage[], defaultStageId = program.defaultStageId) => {
    const validDefault = next.some((stage) => stage.id === defaultStageId)
      ? defaultStageId
      : next[0]?.id;
    onChange({ pipeline: next, defaultStageId: validDefault });
  };

  const reorder = (from: number, to: number) => commit(moveItem(stages, from, to));
  const drag = useDragReorder(reorder);

  const update = (id: string, patch: Partial<PipelineStage>) =>
    commit(stages.map((stage) => (stage.id === id ? { ...stage, ...patch } : stage)));

  const remove = (id: string) => commit(stages.filter((stage) => stage.id !== id));

  const add = () => {
    const stage = createStage('');
    commit([...stages, stage]);
    setExpandedId(stage.id);
  };

  /** Applying a template can strip stages that live applications sit on. */
  const applyTemplate = (incoming: PipelineStage[], label: string) => {
    if (strategy === 'overwrite') {
      const lost = stagesLostByOverwrite(stages, incoming);
      if (lost.length > 0) {
        setPendingWarning({ stages: incoming, lost, label });
        return;
      }
    }
    commit(applyPipelineTemplate(stages, incoming, strategy));
    setNotice(`${label} applied (${strategy}).`);
    setTemplateOpen(false);
  };

  const confirmOverwrite = () => {
    if (!pendingWarning) return;
    commit(applyPipelineTemplate(stages, pendingWarning.stages, 'overwrite'));
    setNotice(`${pendingWarning.label} applied (overwrite).`);
    setPendingWarning(null);
    setTemplateOpen(false);
  };

  const handleExport = () => {
    const template = exportPipelineTemplate(stages, {
      name: `${program.name} pipeline`,
      programId: program.id,
      programName: program.name,
      defaultStageId: program.defaultStageId,
    });
    const blob = new Blob([serialisePipelineTemplate(template)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${program.code || program.id}-pipeline.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (file: File) => {
    const text = await file.text();
    const result = parsePipelineTemplate(text);
    if ('error' in result) {
      setNotice(result.error);
      return;
    }
    applyTemplate(result.template.stages, result.template.name);
  };

  return (
    <section className="space-y-3">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Master pipeline</h3>
          <p className="text-xs text-slate-500">
            The workflow applications follow in <strong>{program.name}</strong>. Drag to reorder.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setTemplateOpen((open) => !open)}
            aria-expanded={templateOpen}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Copy className="h-3.5 w-3.5" /> Templates
          </button>
          <button
            type="button"
            onClick={add}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-xl btn-primary px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" /> Add stage
          </button>
        </div>
      </header>

      {usingDefault && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            This program has no pipeline of its own, so it currently inherits the platform default
            shown below. Editing any stage saves a program-specific copy.
          </span>
        </p>
      )}

      {notice && (
        <p className="rounded-xl border border-blue-200 bg-brand-active px-3 py-2 text-xs font-semibold text-brand">
          {notice}
        </p>
      )}

      {/* ─── Template panel ─────────────────────────────────────────────── */}
      {templateOpen && (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              When applying
            </span>
            {(['merge', 'overwrite'] as PipelineMergeStrategy[]).map((option) => (
              <label key={option} className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                <input
                  type="radio"
                  name="pipeline-strategy"
                  checked={strategy === option}
                  onChange={() => setStrategy(option)}
                  className="text-brand focus:ring-brand-primary"
                />
                {option === 'merge' ? 'Merge (keep existing stages)' : 'Overwrite (replace all)'}
              </label>
            ))}
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Copy from another program
            </p>
            {otherPrograms.length === 0 ? (
              <p className="text-xs text-slate-500">
                No other program has a custom pipeline configured yet.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {otherPrograms.map((source) => (
                  <button
                    key={source.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => applyTemplate(source.pipeline, `${source.name} pipeline`)}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-blue-300 hover:text-brand"
                  >
                    {source.name}
                    <span className="ml-1.5 text-[11px] text-slate-500">
                      {source.pipeline.length} stages
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Start from a preset
            </p>
            <div className="grid gap-2 sm:grid-cols-3">
              {PIPELINE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => applyTemplate(preset.stages, preset.name)}
                  className="rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-blue-300"
                >
                  <p className="text-xs font-bold text-slate-800">{preset.name}</p>
                  <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{preset.description}</p>
                  <p className="mt-1 text-[10px] font-semibold text-slate-400">
                    {preset.stages.length} stages
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3">
            <button
              type="button"
              onClick={() => applyTemplate(stages.map((s) => ({ ...s })), 'Duplicated pipeline')}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Copy className="h-3.5 w-3.5" /> Duplicate current
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Download className="h-3.5 w-3.5" /> Export JSON
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Upload className="h-3.5 w-3.5" /> Import JSON
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleImport(file);
                event.target.value = '';
              }}
            />
          </div>
        </div>
      )}

      {/* ─── Overwrite confirmation ─────────────────────────────────────── */}
      {pendingWarning && (
        <div className="space-y-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="flex items-start gap-2 text-xs font-semibold text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Overwriting removes {pendingWarning.lost.length} stage
              {pendingWarning.lost.length === 1 ? '' : 's'} that applications may currently sit on:{' '}
              <strong>{pendingWarning.lost.map((s) => s.label).join(', ')}</strong>. Merging keeps
              them.
            </span>
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPendingWarning(null)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                commit(applyPipelineTemplate(stages, pendingWarning.stages, 'merge'));
                setNotice(`${pendingWarning.label} merged.`);
                setPendingWarning(null);
                setTemplateOpen(false);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
            >
              Merge instead
            </button>
            <button
              type="button"
              onClick={confirmOverwrite}
              className="rounded-xl bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700"
            >
              Overwrite anyway
            </button>
          </div>
        </div>
      )}

      {/* ─── Live preview ───────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-50/80 p-2">
        <div className="flex min-w-max items-stretch gap-1">
          {stages.filter((s) => !s.archived).map((stage, index, visible) => (
            <span
              key={stage.id}
              className="flex h-9 min-w-[110px] items-center justify-center rounded-lg px-3 text-[11px] font-bold text-white"
              style={{ background: stage.color || stageColor(index, visible.length) }}
              title={stage.description || stage.label}
            >
              {stage.label || 'Untitled'}
            </span>
          ))}
          {stages.filter((s) => !s.archived).length === 0 && (
            <span className="px-3 py-2 text-xs text-slate-500">No active stages</span>
          )}
        </div>
      </div>

      {/* ─── Stage list ─────────────────────────────────────────────────── */}
      <ul className="space-y-2">
        {stages.map((stage, index) => {
          const itemProps = drag.getItemProps(index);
          const expanded = expandedId === stage.id;
          const isDefault = (program.defaultStageId || stages[0]?.id) === stage.id;
          return (
            <li
              key={stage.id}
              {...itemProps}
              className={dragRowClass(itemProps['data-dragging'], itemProps['data-drag-over'])}
            >
              <div className="flex items-start gap-2 p-3">
                <span className="mt-1.5 cursor-grab text-slate-300 active:cursor-grabbing" aria-hidden="true">
                  <GripVertical className="h-4 w-4" />
                </span>

                <span
                  className="mt-1 h-6 w-6 shrink-0 rounded-md"
                  style={{ background: stage.color || stageColor(index, stages.length) }}
                  aria-hidden="true"
                />

                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      value={stage.label}
                      placeholder="Stage name"
                      disabled={disabled}
                      onChange={(event) => update(stage.id, { label: event.target.value })}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    />
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => commit(stages, stage.id)}
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold transition ${
                        isDefault
                          ? 'bg-brand-active text-brand'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {isDefault ? 'Default stage' : 'Set default'}
                    </button>
                  </div>

                  <input
                    value={stage.subtitle || ''}
                    placeholder="Short caption shown in the pipeline bar"
                    disabled={disabled}
                    onChange={(event) => update(stage.id, { subtitle: event.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  />

                  {expanded && (
                    <div className="space-y-3 rounded-xl bg-slate-50 p-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                          Colour
                          <input
                            type="color"
                            value={stage.color || stageColor(index, stages.length)}
                            disabled={disabled}
                            onChange={(event) => update(stage.id, { color: event.target.value })}
                            className="h-7 w-10 cursor-pointer rounded border border-slate-200"
                          />
                        </label>
                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                          Icon
                          <input
                            value={stage.icon || ''}
                            placeholder="plane"
                            disabled={disabled}
                            onChange={(event) => update(stage.id, { icon: event.target.value })}
                            className="w-28 rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                        </label>
                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                          <input
                            type="checkbox"
                            checked={!!stage.terminal}
                            disabled={disabled}
                            onChange={(event) => update(stage.id, { terminal: event.target.checked })}
                            className="rounded border-slate-300 text-brand focus:ring-brand-primary"
                          />
                          Final stage
                        </label>
                      </div>
                      <textarea
                        value={stage.description || ''}
                        placeholder="What happens in this stage? Shown in tooltips and reports."
                        disabled={disabled}
                        rows={2}
                        onChange={(event) => update(stage.id, { description: event.target.value })}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                      />
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-center gap-0.5">
                  <button
                    type="button"
                    aria-label="Move stage up"
                    disabled={disabled || index === 0}
                    onClick={() => reorder(index, index - 1)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move stage down"
                    disabled={disabled || index === stages.length - 1}
                    onClick={() => reorder(index, index + 1)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : stage.id)}
                    aria-expanded={expanded}
                    className="rounded-lg px-2 py-1 text-[11px] font-bold text-brand hover:bg-brand-active"
                  >
                    {expanded ? 'Done' : 'Configure'}
                  </button>
                  <button
                    type="button"
                    aria-label={stage.archived ? 'Restore stage' : 'Archive stage'}
                    title={stage.archived ? 'Restore stage' : 'Archive stage (keeps history)'}
                    disabled={disabled}
                    onClick={() => update(stage.id, { archived: !stage.archived })}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    {stage.archived ? (
                      <ArchiveRestore className="h-3.5 w-3.5" />
                    ) : (
                      <Archive className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${stage.label || 'stage'}`}
                    disabled={disabled}
                    onClick={() => remove(stage.id)}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {stages.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center">
          <Workflow className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-semibold text-slate-700">No stages yet</p>
          <p className="mt-1 text-xs text-slate-500">Add stages or apply a template to get started.</p>
        </div>
      )}
    </section>
  );
}
